import React from 'react';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';

// ---------------------------------------------------------------------------
// LoginBackground
//
// Right-side panel on the sign-in landing page (visible on lg+ screens).
// Rewritten for a first-time visitor who might be evaluating the app
// for their district — three concrete things a special-ed teacher does
// with it, in plain English, without marketing puffery like "10,000
// students tracked" (a number the visitor has no way to verify and
// which reads as fluff).
// ---------------------------------------------------------------------------

const FEATURES = [
  {
    icon: 'Target',
    title: 'Track IEP goals',
    body:
      "Write a goal in plain English or start from a template. Log data points quickly and watch the progress trend build across sessions.",
  },
  {
    icon: 'AlertTriangle',
    title: 'Log behavior incidents',
    body:
      "Simple mode is a few taps: pick the behavior, pick what happened before and after. Detailed mode adds full ABC data and function-of-behavior for FBA / BIP work.",
  },
  {
    icon: 'Users',
    title: 'Share with your team',
    body:
      "When the speech therapist, OT, PT, or paraprofessional logs something for one of your students, you see it on your dashboard. No email chains, no missed notes.",
  },
];

const LoginBackground = () => {
  return (
    <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
      <div className="absolute inset-0">
        <Image
          src="https://images.unsplash.com/photo-1497486751825-1233686d5d80?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80"
          alt="Special education classroom"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-primary/90 to-secondary/80" />
      </div>

      <div className="relative z-10 flex flex-col justify-center p-12 text-white w-full">
        <div className="max-w-md">
          <h2 className="text-4xl font-bold mb-3 leading-tight">
            Everything you track,
            <br />
            in one place.
          </h2>
          <p className="text-lg text-white/85 mb-8">
            Built for special-ed teachers, paras, and related-service
            providers.
          </p>

          <div className="space-y-6">
            {FEATURES.map((feature) => (
              <div key={feature.title} className="flex items-start gap-4">
                <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-white/15 backdrop-blur flex items-center justify-center">
                  <Icon
                    name={feature.icon}
                    size={22}
                    className="text-white"
                  />
                </div>
                <div>
                  <h3 className="text-lg font-semibold mb-1">
                    {feature.title}
                  </h3>
                  <p className="text-sm text-white/85 leading-relaxed">
                    {feature.body}
                  </p>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-10 pt-6 border-t border-white/20">
            <div className="flex items-center gap-2 text-sm text-white/85">
              <Icon name="ShieldCheck" size={16} />
              <span>FERPA-compliant. Your data stays in your database.</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginBackground;
